import { rem } from 'polished';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Spinner } from '@vimeo/bokeh';
import { bokehTheme } from '@vimeo/bokeh-theme';
import { translate } from '@vimeo/i18n-runtime';
import { ArrowLeft, ChevronRight, Comment as ChatIcon } from '@vimeo/iris/icons';
import { Header, Paragraph } from '@vimeo/iris/typography';
import { ESidebarAttach } from '@vimeo/live-core/components/sidebar/ResponsiveSidebar.types';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import { InteractionToolsEntry } from '@vimeo/live-interaction';

import { useLiveChatStatus } from '../../hooks/useLiveChatStatus';
import { useShowcaseEmbed } from '../../hooks/useShowcaseEmbed';
import { AlbumThemes, Clip } from '../../types';
import { LIVE_EVENTS_KEY } from '../../utils/constants';
import { getVideoId, idFromUri } from '../../utils/helpers';

const MOBILE_WIDTH = 767;

type Props = {
  onBack: VoidFunction;
  albumId: number;
  selectedVideo: Clip;
  brandColor: string;
  theme: AlbumThemes;
  onNext: VoidFunction;
  onPrevious: VoidFunction;
  continuousPlay: boolean;
  displayArrows: boolean;
  baseUrl: string;
  jwt: string;
  hashedPass: string | undefined;
};

function LivePlayer({
  onBack,
  albumId,
  selectedVideo,
  brandColor,
  theme,
  onNext,
  onPrevious,
  continuousPlay,
  displayArrows,
  baseUrl,
  jwt,
  hashedPass,
}: Props) {
  const [isLoadingPlayerIframe, setIsLoadingPlayerIframe] = useState(true);
  const [shouldAutoplay, setShouldAutoplay] = useState(true);
  const [displayChat, setDisplayChat] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const { getEmbedQueryParam } = useShowcaseEmbed();

  const [isLiveChatEnabled] = useLiveChatStatus(selectedVideo);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const iFrameRef = useRef<HTMLIFrameElement>(null);

  const avatarSrcSet = selectedVideo.user.pictures.sizes
    .map((thumb) => `${thumb.link} ${thumb.width}w`)
    .join(', ');
  const selectedVideoId = getVideoId(selectedVideo);
  const playerDescriptionHeight =
    playerContainerRef && playerContainerRef.current && isLiveChatEnabled && isMobile
      ? window.innerHeight - playerContainerRef.current.clientHeight - 40 // height - player size - labels size
      : 'unset';
  const sessionId = idFromUri(selectedVideo.live?.recurringEvent?.uri as string, LIVE_EVENTS_KEY);
  const unlistedHash = selectedVideo.live?.recurringEvent?.streamPrivacy?.unlistedHash;
  const roomHashedPassword =
    selectedVideo.live?.recurringEvent?.album?.id === albumId ? hashedPass : undefined;
  const isChatVisible: boolean = isLiveChatEnabled && displayChat && Boolean(sessionId);

  let iframeUrl = useMemo(
    () =>
      `${baseUrl}/showcase/${albumId}/video/${selectedVideoId}/embed?jwt=${jwt}&autoplay=${
        shouldAutoplay || continuousPlay ? 1 : 0
      }${hashedPass ? `&hash=${hashedPass}` : ''}${getEmbedQueryParam()}`,
    [baseUrl, albumId, selectedVideoId, shouldAutoplay, continuousPlay, hashedPass],
    // Not added jwt to dependency to avoid update the url in every 14 minutes, it will reload the iframe and video which is creating issue while watching long video.
  );

  useEffect(() => {
    window.addEventListener('message', handleIframePostMessage);
    window.addEventListener('resize', displayChatOnMobile);
    displayChatOnMobile();

    return () => {
      window.removeEventListener('message', handleIframePostMessage);
      window.removeEventListener('resize', displayChatOnMobile);
    };
  });

  useEffect(() => {
    setIsLoadingPlayerIframe(true);
    setShouldAutoplay(false);
  }, [selectedVideo.uri]);

  const handleIframePostMessage = (event: { data: string }) => {
    if (event.data === 'PLAYER_ENDED') {
      onNext();
    }
  };

  const displayChatOnMobile = () => {
    if (window.innerWidth <= MOBILE_WIDTH && !isMobile) {
      setIsMobile(true);
    }

    if (window.innerWidth > MOBILE_WIDTH && isMobile) {
      setDisplayChat(false);
      setIsMobile(false);
    }
  };

  const onLoad = () => {
    if (iFrameRef.current && iFrameRef.current.contentWindow) {
      iFrameRef.current.contentWindow.postMessage('ATTACH_PLAYER_EVENTS', '*');
    }
    setIsLoadingPlayerIframe(false);
  };

  return (
    <>
      <LivePlayerContainer ref={playerContainerRef}>
        <BackArrow onClick={onBack} brandColor={brandColor}>
          <ArrowLeft />
          <ParagraphNoMargin size="1">{translate('Back')}</ParagraphNoMargin>
        </BackArrow>
        <VideoWrapper isChatVisible={isChatVisible}>
          {isLoadingPlayerIframe && <CenteredLoader />}
          <iframe
            onLoad={onLoad}
            ref={iFrameRef}
            scrolling="no"
            allowFullScreen
            allow="autoplay"
            src={iframeUrl}
            title={selectedVideo.name}
          />
          {displayArrows && (
            <div>
              <PreviousButton theme={theme} onClick={onPrevious}>
                <PreviousIcon />
              </PreviousButton>
              <NextButton theme={theme} onClick={onNext}>
                <ChevronRight />
              </NextButton>
            </div>
          )}
          {isLiveChatEnabled && (
            <ChatIconWrapper
              onClick={() => {
                setDisplayChat(!displayChat);
              }}
            >
              <ChatIcon />
            </ChatIconWrapper>
          )}
        </VideoWrapper>

        {isChatVisible && !isMobile && (
          <LiveChatWrapper>
            <InteractionToolsEntry
              isStandalone={true} // temp for quick fix, should be more explicit name for init open state
              isActiveStatusObservingEnabled={false}
              isCustomThemeEnabled={false}
              isFooterEnabled={false}
              isManagementDisabled={true}
              isCollapsibleSignIn={true}
              isBypassStatusObservingEnabled={true}
              sessionType={EComposerSessionType.LIVE_EVENT}
              sessionId={sessionId}
              roomHashedPassword={roomHashedPassword}
              roomUnlistedHash={unlistedHash}
              toolbarPanelMaxSize={400}
            />
          </LiveChatWrapper>
        )}
      </LivePlayerContainer>
      <BottomWrapper style={{ minHeight: playerDescriptionHeight }}>
        <TextWrapper theme={theme}>
          <Title>{selectedVideo.name}</Title>
          <ProfileLine theme={theme}>
            {avatarSrcSet && (
              <AvatarImg
                src={selectedVideo.user.pictures.sizes[0].link}
                srcSet={avatarSrcSet}
                alt=""
              />
            )}
            <Paragraph size="2">{selectedVideo.user.name}</Paragraph>
          </ProfileLine>
          <Paragraph size="2">{selectedVideo.description}</Paragraph>
        </TextWrapper>
        {isLiveChatEnabled && isMobile && (
          <MobileChatWrapper>
            <InteractionToolsEntry
              isStandalone={true} // temp for quick fix, should be more explicit name for init open state
              isActiveStatusObservingEnabled={false}
              isCustomThemeEnabled={false}
              isFooterEnabled={false}
              isManagementDisabled={true}
              isCollapsibleSignIn={true}
              isBypassStatusObservingEnabled={true}
              sessionType={EComposerSessionType.LIVE_EVENT}
              sessionId={sessionId}
              roomHashedPassword={roomHashedPassword}
              roomUnlistedHash={unlistedHash}
              toolbarPanelMaxSize={600}
              toolbarAttachPosition={ESidebarAttach.BOTTOM}
            />
          </MobileChatWrapper>
        )}
      </BottomWrapper>
    </>
  );
}

export default LivePlayer;

const LiveChatWrapper = styled.div`
  flex-shrink: 0;
  background: white;
  position: relative;
  margin-top: ${rem(70)};
  width: ${rem(400)};
`;

const MobileChatWrapper = styled.div`
  background: white;
  position: relative;
  flex-grow: 1;
  flex-shrink: 0;
  min-height: ${rem(600)};
`;

const ChatIconWrapper = styled.div`
  padding: ${rem(10)};
  background: rgba(0, 0, 0, 0.3);
  width: ${rem(50)};
  height: ${rem(50)};
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 0;

  svg path {
    fill: white;
  }

  @media only screen and (max-width: 960px) {
    width: ${rem(50)};
    height: ${rem(50)};
  }

  @media only screen and (max-width: 767px) {
    display: none;
  }
`;

const PreviousIcon = styled(ChevronRight)`
  transform: rotate(180deg);
`;

const LivePlayerContainer = styled.div`
  display: flex;
  position: relative;
  height: calc(70vw * 9 / 16 + ${rem(70)});

  @media only screen and (max-width: 767px) {
    height: calc(100vw * 9 / 16 + ${rem(70)});
  }
`;

const BackArrow = styled.div<{ brandColor: string }>`
  position: absolute;
  top: ${rem(20)};
  left: ${rem(30)};
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: ${rem(30)};
    height: ${rem(30)};
    padding-right: ${rem(10)};
    path {
      fill: ${(props) => (props.brandColor ? props.brandColor : bokehTheme.colors.blue['500'])};
    }
  }
`;

const VideoWrapper = styled.div<{ isChatVisible: boolean }>`
  width: 70vw;
  height: calc(100% - ${rem(70)});
  position: relative;
  margin: ${(props) => (props.isChatVisible ? `${rem(70)} 0 0` : `${rem(70)} auto 0`)};
  flex-grow: ${(props) => (props.isChatVisible ? '1' : '0')};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }

  @media only screen and (max-width: 767px) {
    width: 100%;
  }
`;

const TextWrapper = styled.div<{ theme: AlbumThemes }>`
  margin: ${rem(50)} auto;
  padding: ${rem(40)} 0;
  width: 70vw;
  color: ${(props) => (props.theme === 'dark' ? 'white' : 'black')};
  flex-grow: 0;

  p {
    color: inherit;
  }

  @media only screen and (max-width: 767px) {
    padding: ${rem(20)};
    margin: 0;
  }
`;

const Title = styled(Header).attrs({ size: '1' })`
  font-size: calc(2rem + 3.5vw);
  font-weight: 800;
  color: inherit;
  font-size: ${rem(55)};
  line-height: ${rem(60)};
  letter-spacing: ${rem(-1.5)};

  @media only screen and (max-width: 1024px) {
    font-size: ${rem(40)};
    line-height: ${rem(46)};
    letter-spacing: ${rem(-1)};
  }

  @media only screen and (max-width: 767px) {
    font-size: ${rem(30)};
    line-height: ${rem(34)};
    letter-spacing: ${rem(-0.75)};
  }
`;

const ParagraphNoMargin = styled(Paragraph)`
  margin: 0;
`;

const ProfileLine = styled.div<{ theme: AlbumThemes }>`
  display: flex;

  p {
    color: ${(props) => (props.theme === 'dark' ? 'white' : 'black')};
  }
`;

const AvatarImg = styled.img`
  width: ${rem(30)};
  height: ${rem(30)};
  border-radius: 50%;
  margin-right: ${rem(15)};
`;

const CenteredLoader = styled(Spinner)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const arrowStyle = css<{ theme: AlbumThemes }>`
  width: 5vw;
  z-index: 5;
  opacity: 0.25;
  cursor: pointer;

  path {
    fill: ${(props) => (props.theme === 'dark' ? 'white' : 'black')};
  }

  &:hover {
    opacity: 1;
  }
`;

const PreviousButton = styled.div<{ theme: AlbumThemes }>`
  position: absolute;
  top: 42%;
  left: -14%;

  svg {
    ${arrowStyle};
  }

  @media only screen and (max-width: 767px) {
    display: none;
  }
`;

const NextButton = styled.div<{ theme: AlbumThemes }>`
  position: absolute;
  top: 42%;
  right: -14%;

  svg {
    ${arrowStyle};
  }

  @media only screen and (max-width: 767px) {
    display: none;
  }
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 767px) {
    overflow: scroll;
  }
`;
